import { useState } from 'preact/hooks';
import { AppIcon } from './AppIcon';
import { FieldHeader } from './FieldHeader';
import { Tooltip } from './Tooltip';
import { cleanWardNames, MAX_WARD_NAMES } from '../../shared/validate';
import { sortWardNames } from '../../shared/wards';

interface Props {
  value: string[];
  onChange: (names: string[], pending?: string) => void;
  draftValue?: string;
  onDraftChange?: (value: string) => void;
  error?: string;
  suggestions: string[];
  /** Shown when the list was filled in from an earlier event. */
  remembered?: boolean;
}

/** A list of ward names with per-item remove buttons and an add box. Commas or new lines add several at once. */
export function WardNamesEditor({ value, onChange, suggestions, remembered, draftValue, onDraftChange, error }: Props) {
  const [localDraft, setDraft] = useState('');
  const draft = draftValue ?? localDraft;
  const add = () => {
    const parts = draft.split(/[,\n]/);
    const next = sortWardNames(cleanWardNames([...value, ...parts]).slice(0, MAX_WARD_NAMES));
    if (draftValue !== undefined || next.length !== value.length) onChange(next, '');
    setDraft('');
  };
  const names = sortWardNames(value);
  const available = sortWardNames(suggestions.filter((s) => !value.some((v) => v.toLowerCase() === s.toLowerCase())));
  return (
    <div class="stack tight">
      <FieldHeader htmlFor="ward-entry" label="Ward names" error={error} errorId="ward-entry-error" />
      <span class="muted small">Participants choose their ward from this list when they register. {remembered && 'Filled in from your last stake event; add or remove wards as needed.'}</span>
      {value.length > 0 ? (
        <ul class="chips" aria-label="Ward names">
          {names.map((w) => (
            <li class="chip" key={w}>
              <span>{w}</span>
              <Tooltip text={`Remove ${w}`}><button type="button" class="chip-x" aria-label={`Remove ${w}`} onClick={() => onChange(value.filter((x) => x !== w))}><AppIcon name="close" /></button></Tooltip>
            </li>
          ))}
        </ul>
      ) : (
        <span class="muted small">No wards yet. Without a list, participants are not asked for their ward.</span>
      )}
      <div class="row">
        <input id="ward-entry" aria-label="Add a ward name" list="ward-name-list-suggestions" autoComplete="off" placeholder="Add a ward, e.g. Brushy Creek" value={draft}
          aria-invalid={!!error} aria-describedby={error ? 'ward-entry-error' : undefined}
          onInput={(e) => { const text = e.currentTarget.value; setDraft(text); onDraftChange?.(text); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} style="flex:1" />
        <button type="button" class="secondary" onClick={add} disabled={!draft.trim()}>Add</button>
      </div>
      <datalist id="ward-name-list-suggestions">{available.map((v) => <option value={v} />)}</datalist>
    </div>
  );
}
